import React from 'react'
import {Dropdown, Button} from '@/components/atoms'
import styles from '../RewardSetup.module.scss'
import BonusProductsCondition from './BonusProductsCondition'
import DigitalCondition from './DigitalCondition'
import ProductOrPepVoucherCondition from './ProductOrPepVoucherCondition'
import VisaCard from './RewardsCondition/VisaCard/VisaCard'
import PointsCondition from './PointsCondition'
import LoyaltyUrl from './LoyaltyUrl'
import RewardPreview from './RewardPreview'
import CashDiscountCondition from './CashDiscountCondition'
import {REWARD_TYPES} from '@/utils/constants/rewardsConstants'

/**
 * RewardConditionsCard component displays the reward conditions section
 * Contains reward type dropdown, points input, and reset button
 * For BONUS_PRODUCTS reward type, shows different structure with product selection modal, order quantity, and limit
 *
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data containing rewardType, points, selectedProducts, orderQuantity, bonusProductLimit
 * @param {Object} props.validation - Validation state object
 * @param {Array} props.rewardTypes - Array of reward type options
 * @param {boolean} props.rewardTypesLoading - Loading state for reward types
 * @param {Function} props.handleRewardTypeChange - Handler for reward type changes
 * @param {Function} props.handlePointsChange - Handler for points input changes
 * @param {Function} props.handleProductsChange - Handler for products selection changes
 * @param {Function} props.handleOrderQuantityChange - Handler for order quantity changes
 * @param {Function} props.handleBonusProductLimitChange - Handler for bonus product limit changes
 * @param {Function} props.handleLoyaltyURLChange - Handler for loyalty URL input changes
 * @param {Function} props.resetRewardConditions - Handler for reset button
 * @param {string} props.pointsError - Error message for points validation
 * @param {string} props.orderQuantityError - Error message for order quantity validation
 * @param {string} props.bonusProductLimitError - Error message for bonus product limit validation
 * @param {string} props.loyaltyURLError - Error message for loyalty URL validation
 * @returns {JSX.Element} The rendered RewardConditionsCard component
 */
const RewardConditionsCard = ({
  formData,
  validation,
  rewardTypes,
  rewardTypesLoading,
  rewardCardTypes,
  rewardCardTypesLoading,
  bonusProductsLoading,
  pepVouchersOrProductsLoading,
  handleRewardTypeChange,
  handlePointsChange,
  handleProductsChange,
  handleOrderQuantityChange,
  handleBonusProductLimitChange,
  handleRewardCardTypeChange,
  handleRedeemPointsChange,
  handleRewardCashAmountChange,
  // Digital specific
  handleDigitalValueChange,
  handleDigitalSupplierChange,
  handleDigitalPointsChange,
  handlePepVouchersChange,
  handleValueChange,
  handleLoyaltyURLChange,
  handleCouponBasedLoyaltyRewardChange,
  resetRewardConditions,
  pointsError,
  orderQuantityError,
  bonusProductLimitError,
  rewardCashAmountError,
  redeemPointsError,
  rewardCardTypeError,
  valueError,
  loyaltyURLError,
  // Digital specific errors
  digitalValueError,
  digitalPointsError,
  digitalSupplierError
}) => {
  // Function to render the appropriate condition component based on reward type
  const renderConditionComponent = () => {
    switch (formData.rewardType) {
      case REWARD_TYPES.BONUS_PRODUCTS:
        return (
          <BonusProductsCondition
            formData={formData}
            validation={validation}
            bonusProductsLoading={bonusProductsLoading}
            handleProductsChange={handleProductsChange}
            handleOrderQuantityChange={handleOrderQuantityChange}
            handleBonusProductLimitChange={handleBonusProductLimitChange}
            orderQuantityError={orderQuantityError}
            bonusProductLimitError={bonusProductLimitError}
          />
        )
      case REWARD_TYPES.DIGITAL:
      case REWARD_TYPES.DIGITAL_PRE_ORDER:
        return (
          <DigitalCondition
            formData={formData}
            handleDigitalValueChange={handleDigitalValueChange}
            handleDigitalSupplierChange={handleDigitalSupplierChange}
            handleDigitalPointsChange={handleDigitalPointsChange}
            digitalValueError={digitalValueError}
            digitalPointsError={digitalPointsError}
            digitalSupplierError={digitalSupplierError}
          />
        )
      case REWARD_TYPES.PRODUCT:
        return (
          <ProductOrPepVoucherCondition
            formData={formData}
            validation={validation}
            pepVouchersOrProductsLoading={pepVouchersOrProductsLoading}
            handleProductsChange={handleProductsChange}
            handlePointsChange={handlePointsChange}
            pointsError={pointsError}
          />
        )
      case REWARD_TYPES.PEP_VOUCHER_PRODUCT:
        return (
          <ProductOrPepVoucherCondition
            formData={formData}
            validation={validation}
            pepVouchersOrProductsLoading={pepVouchersOrProductsLoading}
            handlePepVouchersChange={handlePepVouchersChange}
            handlePointsChange={handlePointsChange}
            pointsError={pointsError}
          />
        )

      case REWARD_TYPES.VISA_CARD:
        return (
          <VisaCard
            formData={formData}
            validation={validation}
            rewardCardTypes={rewardCardTypes}
            rewardCardTypesLoading={rewardCardTypesLoading}
            handleRedeemPointsChange={handleRedeemPointsChange}
            handleRewardCardTypeChange={handleRewardCardTypeChange}
            handleRewardCashAmountChange={handleRewardCashAmountChange}
            redeemPointsError={redeemPointsError}
            rewardCardTypeError={rewardCardTypeError}
            rewardCashAmountError={rewardCashAmountError}
          />
        )

      case REWARD_TYPES.CASH:
      case REWARD_TYPES.DISCOUNT:
      case REWARD_TYPES.PEP_VOUCHER_DISCOUNT:
        return (
          <CashDiscountCondition
            formData={formData}
            handleValueChange={handleValueChange}
            handlePointsChange={handlePointsChange}
            valueError={valueError}
            pointsError={pointsError}
          />
        )

      default:
        return <PointsCondition formData={formData} handlePointsChange={handlePointsChange} pointsError={pointsError} />
    }
  }

  // Render helper for LoyaltyUrl so it's easier to reuse and test
    const renderLoyaltyUrl = () => {
    if (formData?.rewardType !== REWARD_TYPES.LOYALTY) return null
    
    return (
      <LoyaltyUrl
        formData={formData}
        handleLoyaltyURLChange={handleLoyaltyURLChange}
        handleCouponBasedLoyaltyRewardChange={handleCouponBasedLoyaltyRewardChange}
        loyaltyURLError={loyaltyURLError}
      />
    )
  }
  
  return (
    <div className={`rounded-lg p-6 flex flex-col gap-6 ${styles.rewardSetup__card}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold text-white">Reward Conditions*</h3>
        <div className="flex justify-end">
          <Button
            handleButton={resetRewardConditions}
            uiSchema={{
              title: 'Reset',
              variant: 'secondary',
              size: 'small',
            }}
            style={{
              color: '#66A5E7',
              borderColor: '#66A5E7',
            }}
          />
        </div>
      </div>
      <div className="flex flex-col">
        {/* Reward Type Selection */}
        <div className="flex flex-wrap gap-[13px] text-white">
          <span className="text-sm whitespace-nowrap mt-2">Get</span>
          <div className={`${styles.rewardSetup__dropdownWrapper}`}>
            <Dropdown
              className='placeholder:text-sm text-sm'
              value={formData.rewardType ? rewardTypes.filter(rt => rt.value === formData.rewardType) : []}
              updateValue={handleRewardTypeChange}
              isError={validation.rewardTypeValid}
              errorMessage={validation.rewardTypeValid ? 'Reward type is required' : ''}
              isDark={true}
              childList={rewardTypes}
              placeholder={rewardTypesLoading ? "Loading..." : "Select Reward Type"}
              isJsonWrap={false}
              selection='single'
              style={{width:'220px'}}
              hideMenuListOnSelection={true}
              isClearable={false}
            />
          </div>

          {/* Render appropriate condition component */}
          {renderConditionComponent()}
        </div>

        {/* Reward Preview */}
        <RewardPreview formData={formData} />

        {/* Loyalty URL field - shown after preview for LOYALTY reward type */}
        {renderLoyaltyUrl()}
      </div>
    </div>
  )
}

export default RewardConditionsCard
